import { getStatusColor } from '@/core/utils/get_status_color';
import {
  GEO_EVENT_MARKER_SVG_SIZE,
  POLYLINE_STROKE_COLOR,
  POLYLINE_STROKE_OPACITY,
  POLYLINE_STROKE_WEIGHT,
} from '../constants';
import type { TrackingPolygon } from '../hooks/useTrackingPolygons';

function getPolygonCentroid(coords: { lat: number; lng: number }[]): { lat: number; lng: number } {
  let latSum = 0;
  let lngSum = 0;
  for (const c of coords) {
    latSum += c.lat;
    lngSum += c.lng;
  }
  return { lat: latSum / coords.length, lng: lngSum / coords.length };
}

export function drawPolygonsOnMap(
  map: google.maps.Map,
  polygons: TrackingPolygon[]
): { polygons: google.maps.Polygon[]; markers: google.maps.Marker[] } {
  const gmPolygons: google.maps.Polygon[] = [];
  const gmMarkers: google.maps.Marker[] = [];

  for (const polygon of polygons) {
    const gmPolygon = new google.maps.Polygon({
      paths: polygon.coordinates,
      fillColor: polygon.color,
      fillOpacity: 0.15,
      strokeColor: polygon.color,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      map,
    });
    gmPolygons.push(gmPolygon);

    if (polygon.name && polygon.coordinates.length > 0) {
      const centroid = getPolygonCentroid(polygon.coordinates);
      const marker = new google.maps.Marker({
        position: centroid,
        map,
        icon: { path: google.maps.SymbolPath.CIRCLE, scale: 0 },
        label: {
          text: polygon.name,
          color: polygon.color,
          fontWeight: 'bold',
          fontSize: '11px',
        },
        clickable: false,
      });
      gmMarkers.push(marker);
    }
  }

  return { polygons: gmPolygons, markers: gmMarkers };
}

export function clearMapOverlays(overlays: {
  polygons: google.maps.Polygon[];
  markers: google.maps.Marker[];
}) {
  for (const p of overlays.polygons) p.setMap(null);
  for (const m of overlays.markers) m.setMap(null);
}

export function drawPolylineOnMap(
  map: google.maps.Map,
  points: ReadonlyArray<{ lat: number; lng: number }>
): google.maps.Polyline | null {
  const validPoints = points.filter(
    (p) =>
      Number.isFinite(p.lat) &&
      Number.isFinite(p.lng) &&
      Math.abs(p.lat) <= 90 &&
      Math.abs(p.lng) <= 180
  );

  if (validPoints.length < 2) return null;

  return new google.maps.Polyline({
    path: validPoints.map((p) => ({ lat: p.lat, lng: p.lng })),
    strokeColor: POLYLINE_STROKE_COLOR,
    strokeOpacity: POLYLINE_STROKE_OPACITY,
    strokeWeight: POLYLINE_STROKE_WEIGHT,
    geodesic: true,
    map,
  });
}

function createGeoEventMarkerIcon(color: string): string {
  const size = GEO_EVENT_MARKER_SVG_SIZE;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${color}" stroke="white" stroke-width="2"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export interface GeoEventMarkerData {
  lat: number;
  lng: number;
  statusName: string;
  label: string;
  title: string;
}

export function createGeoEventMarkers(
  map: google.maps.Map,
  geoEvents: ReadonlyArray<GeoEventMarkerData>
): google.maps.Marker[] {
  return geoEvents.map((event) => {
    const color = getStatusColor(event.statusName);
    return new google.maps.Marker({
      position: { lat: event.lat, lng: event.lng },
      map,
      icon: {
        url: createGeoEventMarkerIcon(color),
        scaledSize: new google.maps.Size(GEO_EVENT_MARKER_SVG_SIZE, GEO_EVENT_MARKER_SVG_SIZE),
        anchor: new google.maps.Point(GEO_EVENT_MARKER_SVG_SIZE / 2, GEO_EVENT_MARKER_SVG_SIZE / 2),
      },
      label: {
        text: event.label,
        color: '#fff',
        fontSize: '10px',
        fontWeight: 'bold',
      },
      title: event.title,
    });
  });
}
